import { useVisibleSessionStatuses } from '@/components/ui/session-status/use-visible-session-statuses';
import { enumToDescription } from '@/utils/enumToDescription';
import {
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@heidi/design-system/dropdown-menu';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { ChevronRight, Tag } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useStatusFilter } from './hooks/useStatusFilter';
import { StatusFilterItem } from './status-filter-item';

export const StatusFilter = () => {
  const { t } = useTranslations();
  const { isOpen, setIsOpen, selectedStatusesFilter } = useStatusFilter();
  const visibleStatuses = useVisibleSessionStatuses();

  // Sorting logic moved from useStatusFilter
  const [statuses, setStatuses] = useState(visibleStatuses);
  const hasSortedOnOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !hasSortedOnOpen.current) {
      const sortedStatuses = [...visibleStatuses].sort((a, b) => {
        const aIsSelected = selectedStatusesFilter.includes(a);
        const bIsSelected = selectedStatusesFilter.includes(b);

        if (aIsSelected && !bIsSelected) return -1;
        if (!aIsSelected && bIsSelected) return 1;
        return 0;
      });
      setStatuses(sortedStatuses);
      hasSortedOnOpen.current = true;
    } else if (!isOpen) {
      hasSortedOnOpen.current = false;
    }
  }, [isOpen, selectedStatusesFilter, visibleStatuses]);

  return (
    <DropdownMenuGroup>
      <DropdownMenuSub open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuSubTrigger
          hideChevron
          className="max-w-full justify-between gap-2 py-1.5 pl-2.5 pr-1.5"
        >
          <div className="flex items-center gap-2">
            <Tag size={16} className="min-w-4" />
            <div className="flex flex-col justify-center gap-0.5">
              <TypographyP2>{t('sessionToolbar.filters.status')}</TypographyP2>
              {selectedStatusesFilter.length <= 2 &&
                selectedStatusesFilter.length > 0 && (
                  <TypographyCaption className="text-text-secondary">
                    {selectedStatusesFilter
                      .map((status) => {
                        return t(
                          `sessionStatus.${enumToDescription[status]}` as TranslationKey
                        );
                      })
                      .join(', ')}
                  </TypographyCaption>
                )}
              {selectedStatusesFilter.length > 2 && (
                <TypographyCaption className="text-text-secondary">
                  {selectedStatusesFilter.length} {t('sessionToolbar.filters.selected')}
                </TypographyCaption>
              )}
            </div>
          </div>

          <ChevronRight
            size={16}
            className="min-w-4 text-text-secondary rtl:-scale-x-100"
          />
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent alignOffset={-5} sideOffset={4}>
            {statuses.map((status) => (
              <StatusFilterItem key={status} status={status} />
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
    </DropdownMenuGroup>
  );
};
